import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { PageIndex } from '@common/constant/PaginateConstant';
import { Box } from '@mui/material';
import debounce from 'lodash/debounce';
import { useInView } from 'react-intersection-observer';
import { Element } from 'react-scroll';

import CourseDiscussionBoardContent from './courseDiscussionBoardContent';
import CourseDiscussionBoardHeader from './courseDiscussionBoardHeader';

import Container from '@components/store/container';
import { useCourseDiscussionList } from '@hooks/course/courseDiscussion/useCourseDiscussionList';

type CourseDiscussionProps = {
  scrollElementName: string;
  isDisplayCourseDiscussion: boolean;
};

const CourseDiscussionBoard = ({ scrollElementName, isDisplayCourseDiscussion }: CourseDiscussionProps) => {
  const router = useRouter();
  const idName = router.query.idName as string;
  const { handleFetchCourseDiscussionList } = useCourseDiscussionList({ idName });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isInitialFetch, setIsInitialFetch] = useState<boolean>(true);

  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  const handleSearch = useCallback(
    (query: string) => {
      handleFetchCourseDiscussionList({ page: PageIndex, title: query });
    },
    [handleFetchCourseDiscussionList],
  );

  const debouncedHandleSearch = useMemo(() => debounce(handleSearch, 300), [handleSearch]);

  useEffect(() => {
    if (searchQuery) {
      debouncedHandleSearch(searchQuery);
    }
    return () => {
      debouncedHandleSearch.cancel();
    };
  }, [searchQuery, debouncedHandleSearch]);

  useEffect(() => {
    if (isDisplayCourseDiscussion && inView && !searchQuery && isDisplayCourseDiscussion) {
      handleFetchCourseDiscussionList({ page: PageIndex });
      setIsInitialFetch(false);
    }
  }, [handleFetchCourseDiscussionList, isDisplayCourseDiscussion, inView, searchQuery]);

  return (
    <Element name={scrollElementName}>
      <Container>
        <Box sx={{ padding: { xs: '48px 24px 48px 24px', lg: '48px 192px 48px 192px' } }} ref={ref}>
          {inView ? (
            <Box sx={{ maxWidth: '752px', marginLeft: 'auto', marginRight: 'auto' }}>
              <CourseDiscussionBoardHeader
                isInitialFetch={isInitialFetch}
                setSearchQuery={setSearchQuery}
                searchQuery={searchQuery}
                handleFetchCourseDiscussionList={handleFetchCourseDiscussionList}
              />
              <CourseDiscussionBoardContent
                searchQuery={searchQuery}
                handleFetchCourseDiscussionList={handleFetchCourseDiscussionList}
                setSearchQuery={setSearchQuery}
                isDisplayCourseDiscussion={isDisplayCourseDiscussion}
              />
            </Box>
          ) : null}
        </Box>
      </Container>
    </Element>
  );
};

export default CourseDiscussionBoard;
